import type {StoreApi} from 'zustand/vanilla';

import {makeActionLockKey, type ActionLockReason} from '../../types/ActionLockState';
import type {EagGridState, EagGridStore} from '../createEagGridStore';

type ActionLockActions = Pick<
  EagGridStore,
  'lockAction' | 'unlockAction' | 'isActionLocked' | 'clearPromiseLocks' | 'clearLocksForRow'
>;

/**
 * Returns a copy of `map` with every entry whose key matches `predicate` removed.
 * Returns the original `map` unchanged (same reference) when nothing matched, so
 * callers can skip a redundant setState.
 */
function deleteMapEntriesWhere<K, V>(map: Map<K, V>, predicate: (key: K) => boolean): Map<K, V> {
  const next = new Map(map);
  let changed = false;
  for (const key of next.keys()) {
    if (predicate(key)) {
      next.delete(key);
      changed = true;
    }
  }
  return changed ? next : map;
}

/** Action-lock actions. Bodies moved verbatim from createEagGridStore.ts. */
export function createActionLockActions(store: StoreApi<EagGridState>): ActionLockActions {
  return {
    lockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => {
      const state = store.getState();
      const key = makeActionLockKey(rowId, actionKey, reason);
      if (state.actionLocks.has(key)) {
        return;
      }
      const next = new Map(state.actionLocks);
      next.set(key, Date.now());
      store.setState({actionLocks: next});
    },

    unlockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => {
      const state = store.getState();
      const key = makeActionLockKey(rowId, actionKey, reason);
      if (!state.actionLocks.has(key)) {
        return;
      }
      const next = new Map(state.actionLocks);
      next.delete(key);
      store.setState({actionLocks: next});
    },

    isActionLocked: (rowId: string, actionKey: string) => {
      const state = store.getState();
      return (
        state.actionLocks.has(makeActionLockKey(rowId, actionKey, 'promise')) ||
        state.actionLocks.has(makeActionLockKey(rowId, actionKey, 'manual'))
      );
    },

    clearPromiseLocks: () => {
      const state = store.getState();
      const next = deleteMapEntriesWhere(state.actionLocks, (key) => key.endsWith(':promise'));
      if (next === state.actionLocks) {
        return;
      }
      store.setState({actionLocks: next});
    },

    clearLocksForRow: (rowId: string) => {
      const state = store.getState();
      const prefix = `${rowId}:`;
      const next = deleteMapEntriesWhere(state.actionLocks, (key) => key.startsWith(prefix));
      if (next === state.actionLocks) {
        return;
      }
      store.setState({actionLocks: next});
    },
  };
}
